import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { JobShipment, SHIPMENT_STATUSES } from "@/lib/types/job";
import { format } from "date-fns";
import { Edit, Trash2, Calendar } from "lucide-react";

interface ShipmentCardProps {
  shipment: JobShipment;
  deliveryLocation?: any;
  canEdit: boolean;
  onEdit: (shipment: JobShipment) => void;
  onDelete: (shipmentId: string) => void;
}

export const ShipmentCard = ({ 
  shipment, 
  deliveryLocation, 
  canEdit, 
  onEdit, 
  onDelete 
}: ShipmentCardProps) => {
  const statusConfig = SHIPMENT_STATUSES.find(s => s.value === shipment.shipment_status);

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-3 text-sm">
          {/* Carrier */}
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <span className="font-semibold truncate">{shipment.carrier_name || 'Unknown Carrier'}</span>
          </div>

          {/* Tracking Numbers */}
          <div className="flex flex-wrap gap-1.5 items-center min-w-0">
            {shipment.tracking_numbers && shipment.tracking_numbers.length > 0 ? (
              <>
                {shipment.tracking_numbers.slice(0, 2).map((number, index) => (
                  <Badge key={index} variant="outline" className="text-xs px-2 py-0">
                    {number}
                  </Badge>
                ))}
                {shipment.tracking_numbers.length > 2 && (
                  <Badge variant="outline" className="text-xs px-2 py-0">
                    +{shipment.tracking_numbers.length - 2} more
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-muted-foreground text-xs">No tracking</span>
            )}
          </div>

          {/* Dates */}
          <div className="flex gap-3 text-xs shrink-0 ml-auto">
            {shipment.ship_date && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground">Shipped </span>
                  <span className="font-medium">{format(new Date(shipment.ship_date), 'MMM d')}</span>
                </div>
              </div>
            )}
            {shipment.estimated_delivery_date && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground">ETA </span>
                  <span className="font-medium">{format(new Date(shipment.estimated_delivery_date), 'MMM d')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {canEdit && (
            <div className="flex gap-1 ml-2 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(shipment)}
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
              {shipment.shipment_status === 'preparing' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onDelete(shipment.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Notes Section - Optional second line only if notes exist */}
        {shipment.status_notes && (
          <div className="mt-2 pt-2 border-t">
            <p className="text-xs text-muted-foreground line-clamp-2">{shipment.status_notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
